import { useCallback, useEffect, useRef, useState } from 'react';
import type { OutgoingAttachment } from '../../api/comms';
import { ApiError } from '../../api/envelope';
import { REFRESH_FLOOR_MS } from '../../lib/useLoad';
import type { Message } from '../../api/types';
import type { CommsSource } from './source';

const PAGE = 20;
/**
 * A conversation the assistant handled opens whole. The agent taking a bot
 * handover is reading a discussion they had no part in, and the handover note
 * points at things said well above the last screen of it — so paging up to find
 * them is exactly the work this avoids. Matches the server's clamp
 * (MAX_BACK_PAGE in controller/messageCont.js); past it the normal
 * "Load earlier messages" pill takes over.
 */
const FULL_PAGE = 200;

export interface ThreadState {
  messages: Message[];
  loading: boolean;
  error: ApiError | null;
  hasOlder: boolean;
  /** An older page is in flight; the thread stays put while it loads. */
  loadingOlder: boolean;
  loadOlder: () => void;
  /**
   * A manual refresh is in flight. Held past the response by the console's
   * refresh floor, so a fast one is still visibly acknowledged.
   */
  refreshing: boolean;
  /** Re-pull this conversation. Rejects if the fetch failed, thread untouched. */
  reload: () => Promise<void>;
  /** Optimistic send; resolves when the server accepted the message. */
  send: (body: {
    content: string;
    bodyHtml?: string;
    isNote: boolean;
    attachments: OutgoingAttachment[];
    contentAttributes?: Record<string, unknown>;
  }) => Promise<void>;
  retry: (messageId: number) => Promise<void>;
  /**
   * Delete one message, internally. The row STAYS — the server swaps the body
   * for a placeholder and flags it — so this replaces the message in place
   * rather than dropping it: a thread that silently loses a bubble reads as
   * one that lost a reply, which is the opposite of what happened.
   */
  remove: (messageId: number) => Promise<void>;
}

/** Insert keeping ascending id order (the wire order), deduped by id. */
function upsertMessage(list: Message[], message: Message): Message[] {
  const index = list.findIndex((m) => m.id === message.id);
  if (index !== -1) {
    const next = [...list];
    next[index] = message;
    return next;
  }
  return [...list, message].sort((a, b) => a.id - b.id);
}

/**
 * One open conversation: latest page + scroll-up paging, optimistic sends
 * reconciled by echoId (REST response and socket event both carry it), and
 * a newest-page re-pull after socket reconnects.
 */
export function useThread(
  source: CommsSource,
  displayId: number | null,
  onSendDenied: () => void,
  /** Open with the whole discussion rather than the latest page. */
  fullHistory = false,
): ThreadState {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const ticket = useRef(0);
  const floorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Latest-value refs for callbacks/subscriptions — synced in an effect (and
  // declared before the load effect so they update first on every commit).
  const displayIdRef = useRef(displayId);
  const messagesRef = useRef(messages);
  // A ref, not a dependency: `botHandled` flips mid-conversation the first time
  // the assistant answers, and re-running the load effect on that would blank
  // and refetch a thread the agent is reading.
  const fullHistoryRef = useRef(fullHistory);
  useEffect(() => {
    displayIdRef.current = displayId;
    messagesRef.current = messages;
    fullHistoryRef.current = fullHistory;
  });

  useEffect(() => () => {
    if (floorTimer.current) clearTimeout(floorTimer.current);
  }, []);

  useEffect(() => {
    const mine = ++ticket.current;
    // A fetch-on-open hook must reset synchronously when the conversation
    // changes — that's its contract, same as useLoad.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMessages([]);
    setError(null);
    setHasOlder(false);
    setLoadingOlder(false);
    if (floorTimer.current) clearTimeout(floorTimer.current);
    setRefreshing(false);
    if (displayId === null) {
      setLoading(false);
      return;
    }
    setLoading(true);
    // Asked for explicitly, so `hasOlder` compares against the page we actually
    // requested rather than a constant that has to match the server's default.
    const size = fullHistoryRef.current ? FULL_PAGE : PAGE;
    source.api.listMessages(displayId, { limit: size }).then(
      (batch) => {
        if (ticket.current !== mine) return;
        setMessages(batch);
        setHasOlder(batch.length === size);
        setLoading(false);
      },
      (e: unknown) => {
        if (ticket.current !== mine) return;
        setError(e instanceof ApiError ? e : new ApiError('server', 0, 'Something went wrong.'));
        setLoading(false);
      },
    );
  }, [source, displayId]);

  const loadOlder = useCallback(() => {
    const id = displayIdRef.current;
    const current = messagesRef.current;
    // Optimistic rows have negative ids — skip them when picking the cursor.
    const oldest = current.find((m) => m.id > 0);
    if (id === null || !oldest) return;
    const mine = ticket.current;
    setLoadingOlder(true);
    // Cleared regardless of the ticket — switching conversations mid-fetch must
    // not leave the next thread's pill stuck on "Loading…".
    source.api.listMessages(id, { before: oldest.id, limit: PAGE }).then(
      (batch) => {
        setLoadingOlder(false);
        if (ticket.current !== mine) return;
        setMessages((existing) => {
          const seen = new Set(existing.map((m) => m.id));
          const fresh = batch.filter((m) => !seen.has(m.id));
          return [...fresh, ...existing];
        });
        setHasOlder(batch.length === PAGE);
      },
      () => {
        /* leave the thread as-is; scrolling up again retries */
        setLoadingOlder(false);
      },
    );
  }, [source]);

  /**
   * Re-pull the newest page and fold it in. One read behind two controls — the
   * refresh button, and a reconnect — because they are one repair: for a while
   * the socket was not to be trusted, and what it would have said is fetched
   * instead. Merging by id is what lets that repair a row already held: a
   * delivery receipt for a message sent just before the link dropped changes
   * nothing but the status of a row the thread has had all along.
   */
  const pullLatest = useCallback(async () => {
    const id = displayIdRef.current;
    if (id === null) return;
    // Deliberately not a NEW ticket: this cancels nothing, it only has to notice
    // that the agent moved to another conversation while it was out.
    const mine = ticket.current;
    const size = fullHistoryRef.current ? FULL_PAGE : PAGE;
    const batch = await source.api.listMessages(id, { limit: size });
    if (ticket.current !== mine) return;
    const held = messagesRef.current;
    // Optimistic rows carry negative ids, so they never win this comparison.
    const newestHeld = held.reduce<number | null>((max, m) => (m.id > (max ?? 0) ? m.id : max), null);
    const oldestFetched = batch.find((m) => m.id > 0)?.id ?? null;
    if (newestHeld !== null && oldestFetched !== null && oldestFetched > newestHeld) {
      // More than a page arrived unseen, so the fresh page does not touch what
      // is on screen. Merging them would render a thread with a hole in the
      // middle that nothing admits to — start from the page instead and let
      // "Load earlier messages" walk back into the rest. Sends still in flight
      // are kept: the request that owns them is still running.
      setMessages([...batch, ...held.filter((m) => m.id < 0)]);
      setHasOlder(true);
    } else {
      setMessages((current) => batch.reduce(upsertMessage, current));
      // The newest page can only answer `hasOlder` when it is also everything
      // we hold; past that the agent has paged up and what sits above their
      // oldest message is not what this request asked about.
      if (newestHeld === null) setHasOlder(batch.length === size);
    }
    // A pull that worked clears the failure that prompted it.
    setError(null);
  }, [source]);

  /**
   * Manual refresh. Sockets keep an open thread live on their own, so the
   * button exists for when they did not: a connection that dropped without the
   * client noticing, or a send whose delivery receipt never arrived. Held past
   * the response by the console's refresh floor, so a fast one is still visibly
   * acknowledged.
   */
  const reload = useCallback(async () => {
    if (displayIdRef.current === null) return;
    const startedAt = Date.now();
    if (floorTimer.current) clearTimeout(floorTimer.current);
    setRefreshing(true);
    try {
      await pullLatest();
    } finally {
      const held = REFRESH_FLOOR_MS - (Date.now() - startedAt);
      // Cleared whatever the ticket says — a spinner still turning on the thread
      // the agent has already left reads as a wedged button.
      if (held <= 0) setRefreshing(false);
      else floorTimer.current = setTimeout(() => setRefreshing(false), held);
    }
  }, [pullLatest]);

  // Realtime: new/updated messages for this conversation + reconnect catch-up.
  useEffect(() => {
    return source.subscribe((event) => {
      const id = displayIdRef.current;
      if (id === null) return;

      if (event.name === 'message.created' || event.name === 'message.updated') {
        const message = event.payload as Message;
        if (message.conversationId !== id) return;
        setMessages((current) => {
          const echoId = message.contentAttributes?.echoId;
          if (echoId) {
            const optimisticIndex = current.findIndex(
              (m) => m.id < 0 && m.contentAttributes?.echoId === echoId,
            );
            if (optimisticIndex !== -1) {
              const next = current.filter((_, i) => i !== optimisticIndex);
              return upsertMessage(next, message);
            }
          }
          return upsertMessage(current, message);
        });
        return;
      }

      if (event.name === 'reconnected') {
        // What the refresh button does, for the same reason. The after-cursor
        // read this used to run brought only what was SAID while the link was
        // down, and walked straight past what CHANGED: a receipt for a message
        // sent just before the drop lands on a row already held, behind the
        // cursor, and its clock stayed on until the agent refreshed by hand.
        pullLatest().catch(() => {
          /* the thread stays as it was; the next event, or the button, retries */
        });
      }
    });
  }, [source, pullLatest]);

  const send = useCallback<ThreadState['send']>(
    async ({ content, bodyHtml, isNote, attachments, contentAttributes }) => {
      const id = displayIdRef.current;
      if (id === null) return;
      // The conversation this send belongs to, held by ticket: the agent may
      // move on while the request is out, and this hook is not remounted per
      // conversation in every layout. Without the check the reply landed in
      // whichever thread was open by the time the server answered — the
      // socket still delivers it to the right one.
      const mine = ticket.current;
      const echoId = crypto.randomUUID();
      const optimistic: Message = {
        id: -Date.now(),
        conversationId: id,
        inboxId: 0,
        messageType: 'outgoing',
        contentType: 'text',
        content,
        private: isNote,
        status: 'pending',
        sourceId: null,
        senderType: 'agent',
        senderId: null,
        // The agent's own composition, so it can render before the round trip;
        // the server's sanitized copy replaces it when the save comes back.
        contentAttributes: bodyHtml
          ? { echoId, email: { htmlContent: { sanitized: bodyHtml } } }
          : { echoId },
        attachments: [],
        externalCreatedAt: null,
        createdAt: new Date().toISOString(),
      };
      setMessages((current) => [...current, optimistic]);

      try {
        const saved = await source.api.sendMessage(id, {
          content,
          ...(bodyHtml ? { bodyHtml } : {}),
          private: isNote,
          echoId,
          ...(attachments.length ? { attachments } : {}),
          ...(contentAttributes ? { contentAttributes } : {}),
        });
        if (ticket.current !== mine) return;
        setMessages((current) => {
          const withoutOptimistic = current.filter(
            (m) => !(m.id < 0 && m.contentAttributes?.echoId === echoId),
          );
          // The socket can beat this response: delivery is one queue hop from
          // the insert, and on a fast channel the receipt — `message.updated`,
          // status sent — lands by id before the POST returns. This copy was
          // read at commit and still says pending; written over the socket's,
          // it puts the clock back on a message the customer already has, and
          // nothing later takes it off. Whatever the socket delivered is at
          // least as fresh, so a row already held is kept as it stands.
          return withoutOptimistic.some((m) => m.id === saved.id)
            ? withoutOptimistic
            : upsertMessage(withoutOptimistic, saved);
        });
      } catch (e) {
        setMessages((current) =>
          current.filter((m) => !(m.id < 0 && m.contentAttributes?.echoId === echoId)),
        );
        if (e instanceof ApiError && e.kind === 'forbidden') onSendDenied();
        throw e;
      }
    },
    [source, onSendDenied],
  );

  const retry = useCallback(
    async (messageId: number) => {
      const id = displayIdRef.current;
      if (id === null) return;
      await source.api.retryMessage(id, messageId);
      setMessages((current) =>
        current.map((m) => (m.id === messageId ? { ...m, status: 'pending' } : m)),
      );
    },
    [source],
  );

  const remove = useCallback(
    async (messageId: number) => {
      const id = displayIdRef.current;
      if (id === null) return;
      const deleted = await source.api.deleteMessage(id, messageId);
      // From the response, not a local guess at the placeholder: the server
      // decides the wording, and a still-pending send is also flipped to failed
      // there so the deletion never ships.
      setMessages((current) => current.map((m) => (m.id === messageId ? deleted : m)));
    },
    [source],
  );

  return {
    messages,
    loading,
    error,
    hasOlder,
    loadingOlder,
    loadOlder,
    refreshing,
    reload,
    send,
    retry,
    remove,
  };
}
